import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "framer-motion";
import { gapContainer, gapItem, gapItemStatic, gapViewport } from "@/components/site/gap-motion";

interface AdvisorCTAProps {
  eyebrow?: string;
  title: string;
  body?: string;
  primaryLabel?: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  /** Optional photo backdrop; when set, renders behind a 91% purple overlay. */
  backgroundImage?: string;
  /** Runs just before the primary link navigates. */
  onPrimaryClick?: () => void | Promise<void>;
  /** Runs just before the secondary link navigates. */
  onSecondaryClick?: () => void | Promise<void>;
  /** Opt-in: render immediately, no scroll fade-in. Default false. */
  disableAnimation?: boolean;
}

export function AdvisorCTA({
  title,
  body,
  primaryLabel = "View the Course Catalog",
  primaryTo = "/course-catalog/",
  secondaryLabel = "Talk to a Development Pro",
  secondaryTo = "/contact/",
  backgroundImage,
  onPrimaryClick,
  onSecondaryClick,
  disableAnimation = false,
}: AdvisorCTAProps) {
  const reduceMotion = useReducedMotion();
  const still = reduceMotion || disableAnimation;
  const item = still ? gapItemStatic : gapItem;

  return (
    <section
      className={`relative overflow-hidden text-primary-foreground ${backgroundImage ? "" : "bg-[#693D94]"}`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {backgroundImage && (
        <>
          <img
            src={backgroundImage}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#693D94] opacity-[0.91]" aria-hidden="true" />
        </>
      )}
      <motion.div
        className="relative mx-auto max-w-6xl px-6 py-20 md:py-28"
        initial={disableAnimation ? "visible" : "hidden"}
        animate={disableAnimation ? "visible" : undefined}
        whileInView="visible"
        viewport={gapViewport}
        variants={gapContainer}
      >
        <motion.h2
          variants={item}
          className="max-w-3xl text-3xl font-semibold leading-tight text-primary-foreground md:text-4xl"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {title}
        </motion.h2>
        {body && (
          <motion.p
            variants={item}
            className="mt-5 max-w-2xl text-lg leading-relaxed text-primary-foreground/80"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {body}
          </motion.p>
        )}
        <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
          <Link
            to={primaryTo}
            onClick={onPrimaryClick ? () => void onPrimaryClick() : undefined}
            className="inline-flex items-center rounded-[6px] bg-background px-6 py-3 text-sm font-semibold text-primary shadow-brand transition-transform hover:-translate-y-0.5"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {primaryLabel}
          </Link>
          <Link
            to={secondaryTo}
            onClick={onSecondaryClick ? () => void onSecondaryClick() : undefined}
            className="inline-flex items-center rounded-[6px] border border-primary-foreground/40 px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-foreground/10"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {secondaryLabel}
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
